import React, {useMemo, useRef, useState} from "react";
import {KatButton, KatInput, KatModal} from "@amzn/katal-react";
import CustomDropdown from "src/app/component/common/CustomDropdown";

interface SLPTicketModalProps {
    visible: boolean;
    defects: Array<{opportunityId: string; trackingId?: string; stationCode?: string; defectDate: string; defectType?: string}>;
    onSubmit: (category: string, type: string, item: string, trackingIds: string[], stationCode: string, defectDate: string) => Promise<{ticketLink?: string; failureReason?: string; status: string}>;
    onClose: () => void;
}

type ModalState = 'form' | 'submitting' | 'success' | 'failed';

const SLPTicketModal: React.FC<SLPTicketModalProps> = ({visible, defects, onSubmit, onClose}) => {
    const [category, setCategory] = useState("");
    const [type, setType] = useState("");
    const [item, setItem] = useState("");
    const [selectedStation, setSelectedStation] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [modalState, setModalState] = useState<ModalState>('form');
    const [ticketLink, setTicketLink] = useState("");
    const [failureReason, setFailureReason] = useState("");
    const [copied, setCopied] = useState(false);
    const categoryRef = useRef<any>(null);
    const typeRef = useRef<any>(null);
    const itemRef = useRef<any>(null);

    const stationCodes = useMemo(() =>
        Array.from(new Set(defects.filter(d => d.stationCode && d.defectType === "UNRETURNED_PACKAGE").map(d => d.stationCode!))).sort(),
    [defects]);

    const defectDates = useMemo(() => {
        if (!selectedStation) return [];
        return Array.from(new Set(
            defects.filter(d => d.stationCode === selectedStation && d.defectType === "UNRETURNED_PACKAGE").map(d => d.defectDate.substring(0, 10))
        )).sort();
    }, [defects, selectedStation]);

    const matchedTrackingIds = useMemo(() => {
        if (!selectedStation || !selectedDate) return [];
        return Array.from(new Set(
            defects
                .filter(d => d.stationCode === selectedStation && d.defectDate.startsWith(selectedDate) && d.defectType === "UNRETURNED_PACKAGE")
                .map(d => d.trackingId)
                .filter((id): id is string => !!id && id !== "N/A")
        ));
    }, [defects, selectedStation, selectedDate]);

    const handleSubmit = async () => {
        setModalState('submitting');
        try {
            const result = await onSubmit(category, type, item, matchedTrackingIds, selectedStation, selectedDate);
            if (result.status === 'CREATED' && result.ticketLink) {
                setTicketLink(result.ticketLink);
                setModalState('success');
            } else {
                setFailureReason(result.failureReason || '');
                setModalState('failed');
            }
        } catch (err: any) {
            setFailureReason(err.message || '');
            setModalState('failed');
        }
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const resetForm = () => {
        setCategory("");
        setType("");
        setItem("");
        setSelectedStation("");
        setSelectedDate("");
        setModalState('form');
        setTicketLink("");
        setFailureReason("");
        setCopied(false);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(ticketLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const hasUnreturnedPackages = defects.some(d => d.defectType === "UNRETURNED_PACKAGE");

    const isValid = category && type && item && selectedStation && selectedDate && matchedTrackingIds.length > 0;

    // ——— Success State ———
    if (modalState === 'success') {
        return (
            <KatModal visible={visible} onClose={handleClose}>
                <span slot="title">✅ SLP Ticket Created</span>
                <div className="odq-slp-result">
                    <div className="odq-slp-result__link-row">
                        <a href={ticketLink} target="_blank" rel="noreferrer" className="odq-slp-result__link">
                            {ticketLink}
                        </a>
                        <button className="odq-slp-result__copy" onClick={handleCopy} title="Copy link"
                                data-testid="slp-copy-link">
                            {copied ? '✓' : '⧉'}
                        </button>
                    </div>
                    <div className="odq-slp-result__meta">
                        Station: {selectedStation} &nbsp;|&nbsp; Tracking IDs: {matchedTrackingIds.length}
                    </div>
                </div>
                <div slot="footer" className="kat-group-horizontal">
                    <KatButton variant="primary" label="Done" onClick={handleClose} data-testid="slp-done-btn" />
                </div>
            </KatModal>
        );
    }

    // ——— Failed State ———
    if (modalState === 'failed') {
        return (
            <KatModal visible={visible} onClose={handleClose}>
                <span slot="title">✗ Ticket Creation Failed</span>
                <div className="odq-slp-result">
                    <div className="odq-slp-result__title">Ticket Creation Failed</div>
                    <div className="odq-slp-result__error">{failureReason}</div>
                </div>
                <div slot="footer" className="kat-group-horizontal">
                    <KatButton variant="secondary" label="Cancel" onClick={handleClose} data-testid="slp-cancel-btn" />
                    <KatButton variant="primary" label="Back" onClick={() => setModalState('form')} data-testid="slp-back-btn" />
                </div>
            </KatModal>
        );
    }

    // ——— No Unreturned Packages ———
    if (!hasUnreturnedPackages) {
        return (
            <KatModal visible={visible} onClose={handleClose}>
                <span slot="title">Create Ticket for SLP</span>
                <div className="odq-slp-empty-message">
                    There are no unreturned packages for this case.
                </div>
                <div slot="footer" className="kat-group-horizontal">
                    <KatButton variant="secondary" label="Close" onClick={handleClose} data-testid="slp-no-unreturned-close" />
                </div>
            </KatModal>
        );
    }


    // ——— Form State (default + submitting) ———
    return (
        <KatModal visible={visible} onClose={handleClose}>
            <span slot="title">Create Ticket for SLP</span>
            <div className="odq-exempt-modal">
                <div className="odq-exempt-modal__field">
                    <KatInput label="Category (C)" placeholder="Enter category"
                              value={category} ref={categoryRef}
                              onChange={() => setCategory(categoryRef.current?.value || "")}
                              onKeyup={() => setCategory(categoryRef.current?.value || "")}
                              onInput={() => setCategory(categoryRef.current?.value || "")}
                              data-testid="slp-category" />
                </div>

                <div className="odq-exempt-modal__field">
                    <KatInput label="Type (T)" placeholder="Enter type"
                              value={type} ref={typeRef}
                              onChange={() => setType(typeRef.current?.value || "")}
                              onKeyup={() => setType(typeRef.current?.value || "")}
                              onInput={() => setType(typeRef.current?.value || "")}
                              data-testid="slp-type" />
                </div>

                <div className="odq-exempt-modal__field">
                    <KatInput label="Item (I)" placeholder="Enter item"
                              value={item} ref={itemRef}
                              onChange={() => setItem(itemRef.current?.value || "")}
                              onKeyup={() => setItem(itemRef.current?.value || "")}
                              onInput={() => setItem(itemRef.current?.value || "")}
                              data-testid="slp-item" />
                </div>

                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Station"
                        placeholder="--Select Station--"
                        options={stationCodes.map(c => ({value: c, title: c}))}
                        value={selectedStation}
                        onChange={(v) => { setSelectedStation(v); setSelectedDate(""); }}
                        testId="slp-station-select"
                    />
                </div>

                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Defect Date"
                        placeholder="--Select Date--"
                        options={defectDates.map(d => ({value: d, title: d}))}
                        value={selectedDate}
                        onChange={setSelectedDate}
                        testId="slp-date-select"
                    />
                </div>

                {selectedStation && selectedDate && (
                    <div className="odq-exempt-modal__selected">
                        <strong>Matched Tracking IDs:</strong> {matchedTrackingIds.length} item(s)
                        <div className="odq-exempt-modal__id-list" data-testid="slp-matched-ids">
                            {matchedTrackingIds.map(id => (
                                <span key={id} className="odq-exempt-modal__id-chip">{id}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel" onClick={handleClose}
                           disabled={modalState === 'submitting'}
                           data-testid="slp-cancel-btn" />
                <KatButton variant="primary"
                           label={modalState === 'submitting' ? "Creating..." : "Create Ticket"}
                           disabled={!isValid || modalState === 'submitting'}
                           onClick={handleSubmit}
                           data-testid="slp-submit-btn" />
            </div>
        </KatModal>
    );
};

export default SLPTicketModal;
